import type {
  AdminChatRoomDetailResponse,
  AdminLedgerEntryResponse,
  AdminOrderResponse,
} from '@inflip/contracts/admin';
import {
  DISPUTE_RESOLUTION_LABELS,
  feeRateLabel,
  IP_RIGHT_LABELS,
  LicenseStatus,
  OrderStatus,
  lastUseDateOf,
  VIOLATION_REPORT_RESOLUTION_LABELS,
} from '@inflip/shared';
import { Link, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { AdminChatPanel, chatSendersOf } from '../components/admin-chat-panel';
import { BackLink } from '../components/back-link';
import { EvidenceGallery } from '../components/evidence-gallery';
import { LicenseAmendmentSection } from '../components/license-amendment-section';
import { LicenseSuspensionSection } from '../components/license-suspension-section';
import { NoteItems } from '../components/note-items';
import { OrderActionsPanel } from '../components/order-actions-panel';
import { OrderContractTerms } from '../components/order-contract-terms';
import { OrderProposalHistory } from '../components/order-proposal-history';
import { TerminationBadge } from '../components/termination-badge';
import { Button, ErrorBanner, InfoRow, LoadingRow, Section } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { ORDER_STATUS_TEXT, orderStatusLabel } from '../lib/status-ui';
import { contractZoneNote, formatDate, formatDateTime } from '../lib/time-zone';

const LEDGER_LABELS: Record<AdminLedgerEntryResponse['type'], string> = {
  PAYMENT_RECEIVED: '결제 수취 (구매자→플랫폼)',
  REFUND_PAID: '환불 지급 (플랫폼→구매자)',
  PAYOUT_SENT: '정산 지급 (플랫폼→판매자)',
  PAYOUT_CLAWBACK: '정산 회수 (판매자→플랫폼)',
  ROYALTY_RECEIVED: '로열티 수취 (구매자→플랫폼)',
  ROYALTY_PAYOUT: '로열티 정산 (플랫폼→판매자)',
  ROYALTY_REFUNDED: '로열티 정정 환급 (플랫폼→구매자)',
  ROYALTY_CLAWBACK: '로열티 정정 회수 (판매자→플랫폼)',
  DEPOSIT_UNMATCHED: '주문 외 입금 (구매자→플랫폼, 반환 대상)',
  DEPOSIT_RETURNED: '입금 반환 (플랫폼→구매자)',
};

const ADDON_STATUS_LABEL: Record<string, string> = {
  PENDING: '입금 대기',
  CONFIRMED: '입금 확인',
  CANCELLED: '요청 취소',
  EXPIRED: '기한 만료',
  FAILED: '실패',
};
const ADDON_STATUS_CLS: Record<string, string> = {
  PENDING: 'text-amber-600',
  CONFIRMED: 'text-emerald-600',
  CANCELLED: 'text-muted-foreground',
  EXPIRED: 'text-muted-foreground',
  FAILED: 'text-rose-600',
};

const LICENSE_STATUS_META: Record<LicenseStatus, { label: string; cls: string }> = {
  [LicenseStatus.ACTIVE]: { label: '유효', cls: 'text-emerald-600' },
  [LicenseStatus.EXPIRED]: { label: '만료', cls: 'text-muted-foreground' },
  [LicenseStatus.REVOKED]: { label: '회수됨', cls: 'text-red-600' },
  [LicenseStatus.TERMINATED]: { label: '해지됨', cls: 'text-rose-600' },
  [LicenseStatus.SUSPENDED]: { label: '사용 중지', cls: 'text-rose-600' },
};

/**
 * 라이선스 특약 — 발급은 판매자가 구매자 확인을 받은 초안 그대로 한다 (어드민 대리 발급 없음).
 * 발급 전에는 발급 예정 초안(licenseDraft)과 구매자 확인 여부, 발급 후에는 확정 특약과
 * 사용 중지·해제와 수정 제안(양쪽 동의 시 반영)·이력을 보여준다.
 */
function LicenseTermsSection({
  order,
  token,
  onChanged,
}: {
  order: AdminOrderResponse;
  token: string;
  onChanged: () => Promise<unknown>;
}) {
  const { license, licenseDraft } = order;
  if (!license && !licenseDraft && order.status !== OrderStatus.PAID) return null;

  return (
    <Section title="라이선스 특약" divided>
      {license ? (
        <>
          <InfoRow label="라이선스">
            <span className="font-mono">{license.licenseKey}</span>
            <span className={`ml-1.5 text-xs font-bold ${LICENSE_STATUS_META[license.status].cls}`}>
              {LICENSE_STATUS_META[license.status].label}
            </span>
          </InfoRow>
          <InfoRow label="효력 기간">
            {formatDate(license.startsAt, order.timeZone)} ~{' '}
            {license.expiresAt
              ? `${lastUseDateOf(new Date(license.expiresAt), order.timeZone).replaceAll('-', '. ')}.`
              : order.rightType === 'APPEARANCE'
                ? '합의한 일정의 1회 출연'
                : '무기한'}
          </InfoRow>
          {contractZoneNote(order.timeZone)}
          <div className="py-3 text-sm">
            <p className="text-muted-foreground">구매자 전용 특약</p>
            {license.specialTerms ? (
              <NoteItems text={license.specialTerms} className="mt-1.5 font-medium" />
            ) : (
              <p className="mt-1.5 text-muted-foreground">작성된 특약 없이 발급됐어요.</p>
            )}
          </div>
          <LicenseSuspensionSection order={order} token={token} onChanged={onChanged} />
          <LicenseAmendmentSection order={order} token={token} onChanged={onChanged} />
        </>
      ) : licenseDraft ? (
        <>
          <InfoRow label="사용 시작일 (예정)">
            {licenseDraft.startsAt
              ? `${licenseDraft.startsAt.replaceAll('-', '. ')}.${contractZoneNote(order.timeZone)}`
              : '발급일부터'}
          </InfoRow>
          <InfoRow label="초안 수정일">{formatDateTime(licenseDraft.updatedAt)}</InfoRow>
          <InfoRow label="구매자 확인">
            {licenseDraft.buyerConfirmedAt ? (
              <span className="text-emerald-600">
                {formatDateTime(licenseDraft.buyerConfirmedAt)} 확인
              </span>
            ) : (
              <span className="text-amber-600">확인 대기 — 확인돼야 판매자가 발급할 수 있어요</span>
            )}
          </InfoRow>
          <div className="py-3 text-sm">
            <p className="text-muted-foreground">구매자 전용 특약 (발급 전 초안)</p>
            {licenseDraft.specialTerms ? (
              <NoteItems text={licenseDraft.specialTerms} className="mt-1.5 font-medium" />
            ) : (
              <p className="mt-1.5 text-muted-foreground">아직 특약이 작성되지 않았어요.</p>
            )}
          </div>
        </>
      ) : (
        <p className="py-3 text-sm text-muted-foreground">
          판매자가 아직 발급 예정 내용을 작성하지 않았어요 — 판매자가 올리고 구매자가 확인한 내용
          그대로 판매자가 발급해요.
        </p>
      )}
    </Section>
  );
}

const ORDER_TABS = [
  { key: 'order', label: '주문 정보' },
  { key: 'contract', label: '계약 조건' },
  { key: 'license', label: '라이선스' },
  { key: 'actions', label: '운영 액션' },
  { key: 'ledger', label: '원장' },
  { key: 'chat', label: '채팅' },
] as const;
type OrderTab = (typeof ORDER_TABS)[number]['key'];

function readTabFromHash(): OrderTab {
  const hash = window.location.hash.slice(1);
  return ORDER_TABS.some((t) => t.key === hash) ? (hash as OrderTab) : 'order';
}

/**
 * 주문 상세 탭 — 한 페이지에 길게 이어지던 구역을 나눠 본다.
 * 운영 액션은 분쟁 중이거나 처리 중인 판매자 위반 신고가 있으면 빨간 점, 채팅은 어드민이 참여한 방이면 점으로 알린다
 */
function OrderTabBar({
  tab,
  onChange,
  order,
  chatJoined,
}: {
  tab: OrderTab;
  onChange: (tab: OrderTab) => void;
  order: AdminOrderResponse;
  chatJoined: boolean;
}) {
  const dot: Partial<Record<OrderTab, string>> = {
    ...((order.status === OrderStatus.DISPUTED ||
      order.violationReports?.some((r) => !r.resolution)) && { actions: 'bg-rose-500' }),
    ...(chatJoined && { chat: 'bg-brand' }),
  };
  return (
    <div
      role="tablist"
      aria-label="주문 상세"
      className="sticky top-0 z-10 -mx-6 mt-6 flex gap-1 overflow-x-auto border-b border-border bg-background px-6"
    >
      {ORDER_TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={tab === t.key}
          onClick={() => onChange(t.key)}
          className={`relative shrink-0 px-3 py-3 text-sm font-semibold transition ${
            tab === t.key
              ? 'text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t.label}
          {dot[t.key] && (
            <span
              className={`ml-1 inline-block size-1.5 rounded-full align-middle ${dot[t.key]}`}
            />
          )}
        </button>
      ))}
    </div>
  );
}

export function OrderDetailPage() {
  const { orderId } = useParams({ from: '/orders/$orderId' });
  const { session } = useAdminAuth();
  const [order, setOrder] = useState<AdminOrderResponse | null>(null);
  const [ledger, setLedger] = useState<AdminLedgerEntryResponse[]>([]);
  // 거래방은 어드민 API로 찾는다 — undefined 는 조회 중, null 은 방 없음. 대화창은 어드민이 참여한 방에서만 연다
  const [room, setRoom] = useState<AdminChatRoomDetailResponse | null | undefined>(undefined);
  const [roomError, setRoomError] = useState<string | null>(null);
  const [roomJoining, setRoomJoining] = useState(false);
  const [roomJoinError, setRoomJoinError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 이 주문에 붙은 고객센터 1:1 문의 수 — 분쟁·위반 신고 중인 당사자가 따로 남긴 연락을 놓치지 않게
  const [supportCount, setSupportCount] = useState(0);
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .listSupportInquiries(session.accessToken, { orderId, pageSize: 1 })
      .then((res) => !cancelled && setSupportCount(res.total))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [session, orderId]);
  // 탭 — 주소 해시(#contract 등)에 남겨 새로고침·공유해도 같은 탭
  const [tab, setTab] = useState<OrderTab>(readTabFromHash);
  useEffect(() => {
    const base = location.pathname + location.search;
    history.replaceState(history.state, '', tab === 'order' ? base : `${base}#${tab}`);
  }, [tab]);

  const reload = useCallback(() => {
    if (!session) return Promise.resolve();
    return Promise.all([
      api
        .getOrder(session.accessToken, orderId)
        .then(setOrder)
        .catch((err) => setError(err instanceof Error ? err.message : String(err))),
      api
        .orderLedger(session.accessToken, orderId)
        .then(setLedger)
        .catch(() => setLedger([])),
    ]);
  }, [session, orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setRoom(undefined);
    setRoomError(null);
    api
      .listChatRooms(session.accessToken, { orderId, page: 1, pageSize: 1 })
      .then((rows) => (rows[0] ? api.getChatRoom(session.accessToken, rows[0].id) : null))
      .then((r) => !cancelled && setRoom(r))
      .catch((err) => {
        if (!cancelled) setRoomError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, session]);

  if (!session) return null;

  async function handleRoomJoin() {
    if (!session || !room || roomJoining) return;
    setRoomJoining(true);
    setRoomJoinError(null);
    try {
      await api.joinChatRoom(session.accessToken, room.id);
      // 참여 화면으로 바뀔 때까지 버튼을 막아 둔다
      setRoom(await api.getChatRoom(session.accessToken, room.id));
    } catch (err) {
      setRoomJoinError(err instanceof Error ? err.message : String(err));
    } finally {
      setRoomJoining(false);
    }
  }

  async function handleRoomLeave() {
    if (!session || !room) return;
    await api.leaveChatRoom(session.accessToken, room.id);
    setRoom(await api.getChatRoom(session.accessToken, room.id));
  }

  return (
    <main className="mx-auto w-full max-w-[880px] px-6 py-8">
      <div className="mb-6">
        <BackLink
          fallback="/orders"
          fallbackLabel="주문 목록"
          className="text-sm font-medium text-muted-foreground hover:text-brand"
        />
      </div>

      {error && <ErrorBanner message={error} />}
      {!order && !error && <LoadingRow />}

      {order && (
        <>
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <p className={`text-sm font-bold ${ORDER_STATUS_TEXT[order.status]}`}>
                {orderStatusLabel(order.status, order.cancelledBy)}
              </p>
              {order.status === 'TERMINATED' && order.termination && (
                <TerminationBadge termination={order.termination} />
              )}
            </div>
            <h1 className="mt-1 text-2xl font-extrabold tracking-tight">
              {order.totalAmount.toLocaleString('ko-KR')}
              <span className="ml-1 text-lg font-bold text-muted-foreground">{order.currency}</span>
            </h1>
            {order.addonAmount > 0 && (
              <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                계약 금액 {order.contractAmount.toLocaleString('ko-KR')}원 + 확인된 추가금{' '}
                {order.addonAmount.toLocaleString('ko-KR')}원
              </p>
            )}
            <p className="mt-1 font-mono text-xs text-muted-foreground">{order.id}</p>
            {order.orderGroupId && (order.groupSize ?? 1) > 1 && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                묶음 <span className="font-mono">{order.orderGroupId}</span>
              </p>
            )}
            {supportCount > 0 && (
              <Link
                to="/support"
                search={{ orderId: order.id, status: '' }}
                className="mt-1.5 inline-block text-xs font-semibold text-brand hover:underline"
              >
                이 주문 1:1 문의 {supportCount}건 →
              </Link>
            )}
          </header>

          <OrderTabBar
            tab={tab}
            onChange={setTab}
            order={order}
            chatJoined={!!room?.viewerJoined}
          />
          {tab === 'order' && (
            <>
              <Section title="주문 정보" divided>
                <InfoRow label="리스팅">
                  {order.listingTitle ?? '(삭제된 리스팅)'}
                  <span className="ml-1.5 text-muted-foreground">
                    · {IP_RIGHT_LABELS[order.rightType]}
                  </span>
                </InfoRow>
                <InfoRow label="판매자">
                  {order.sellerId ? (
                    <Link
                      to="/users/$userId"
                      params={{ userId: order.sellerId }}
                      className="font-semibold text-brand hover:underline"
                    >
                      {order.sellerName ?? order.sellerId}
                    </Link>
                  ) : (
                    '—'
                  )}
                </InfoRow>
                <InfoRow label="구매자">
                  <Link
                    to="/users/$userId"
                    params={{ userId: order.buyerId }}
                    className="font-semibold text-brand hover:underline"
                  >
                    {order.buyerName ?? order.buyerId}
                  </Link>
                  {order.buyerEmail && (
                    <span className="ml-1.5 text-muted-foreground">({order.buyerEmail})</span>
                  )}
                </InfoRow>
                {/* 환불·입금 반환 때 계좌 칸을 이 값으로 미리 채운다 */}
                <InfoRow label="구매자 환불 계좌">
                  {order.buyerRefundAccount ? (
                    <span className="font-semibold">
                      {order.buyerRefundAccount.bankName} {order.buyerRefundAccount.accountNumber}
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        · 예금주 {order.buyerRefundAccount.holderName}
                      </span>
                    </span>
                  ) : (
                    <span className="text-amber-700">
                      미등록 — 환불·반환 때 계좌를 직접 받아 적어요
                    </span>
                  )}
                </InfoRow>
                <InfoRow label="입금자명">
                  <span className="font-semibold">{order.payment?.depositReference ?? '—'}</span>
                  {/* 입금 확인 때 은행 내역에서 옮겨 적은 실제 입금자명 — 다르면 확인 근거와 함께 */}
                  {order.payment?.receivedDepositorName && (
                    <span className="ml-1.5 text-muted-foreground">
                      · 은행 입금자명 {order.payment.receivedDepositorName}
                    </span>
                  )}
                  {order.payment?.depositorOverrideReason && (
                    <p className="mt-1 text-xs text-amber-700">
                      입금자명 달라 근거 확인 후 처리 — {order.payment.depositorOverrideReason}
                    </p>
                  )}
                </InfoRow>
                {/* 함께 결제한 주문 — 입금 확인·입금 전 취소는 묶음 전체에 적용된다 */}
                {order.groupOrders && order.groupOrders.length > 1 && (
                  <InfoRow label="묶음 주문">
                    <p className="text-muted-foreground">
                      {order.orderGroupId && (
                        <>
                          <Link
                            to="/orders"
                            search={{ q: order.orderGroupId, from: '', to: '' }}
                            className="font-mono font-semibold text-brand hover:underline"
                          >
                            #{order.orderGroupId.slice(0, 8)}
                          </Link>{' '}
                          ·{' '}
                        </>
                      )}
                      {order.groupOrders.length}건을 함께 주문 · 입금 확인과 입금 전 취소는 묶음
                      전체에 적용돼요
                    </p>
                    <div className="mt-1 space-y-1">
                      {order.groupOrders.map((g) => (
                        <div key={g.id}>
                          {g.id === order.id ? (
                            <span className="font-semibold">
                              {IP_RIGHT_LABELS[g.rightType]} (이 주문)
                            </span>
                          ) : (
                            <Link
                              to="/orders/$orderId"
                              params={{ orderId: g.id }}
                              className="font-semibold text-brand hover:underline"
                            >
                              {IP_RIGHT_LABELS[g.rightType]}
                            </Link>
                          )}
                          <span className="ml-1.5 tabular-nums">
                            {g.contractAmount.toLocaleString('ko-KR')}원
                          </span>
                          <span
                            className={`ml-1.5 text-xs font-bold ${ORDER_STATUS_TEXT[g.status]}`}
                          >
                            {orderStatusLabel(g.status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </InfoRow>
                )}
                {order.addonPayments.length > 0 && (
                  <InfoRow label="추가금">
                    <div className="space-y-1">
                      {order.addonPayments.map((p) => (
                        <div key={p.id}>
                          <span className="font-bold tabular-nums">
                            {p.amount.toLocaleString('ko-KR')}원
                          </span>
                          <span
                            className={`ml-1.5 text-xs font-bold ${ADDON_STATUS_CLS[p.status] ?? 'text-muted-foreground'}`}
                          >
                            {ADDON_STATUS_LABEL[p.status] ?? p.status}
                          </span>
                          {p.depositReference && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              {p.depositReference}
                            </span>
                          )}
                          {p.memo && (
                            <span className="ml-1.5 text-xs text-muted-foreground">· {p.memo}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </InfoRow>
                )}
                <InfoRow label="생성일">{formatDateTime(order.createdAt)}</InfoRow>
                <InfoRow label="플랫폼 수수료">
                  {feeRateLabel(order.platformFeeRateBp)}
                  <span className="ml-1 text-muted-foreground">(주문 시점 고정)</span>
                </InfoRow>
                {/* 구매확정 — 확정되면 정산 시작. 기한이 지나면 자동 확정, 분쟁 중이면 멈춤 */}
                {(order.purchaseConfirmedAt || order.purchaseConfirmDueAt) && (
                  <InfoRow label="구매확정">
                    {order.purchaseConfirmedAt ? (
                      <span className="font-semibold text-emerald-600">
                        {order.purchaseConfirmedBy === 'SYSTEM'
                          ? '자동 확정'
                          : order.purchaseConfirmedBy === 'ADMIN'
                            ? `관리자 확정 (${order.status === 'TERMINATED' ? '계약 해지' : '분쟁 기각'})`
                            : '구매자 확정'}{' '}
                        · {formatDateTime(order.purchaseConfirmedAt)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        대기 · {formatDateTime(order.purchaseConfirmDueAt!, order.timeZone)}
                        {contractZoneNote(order.timeZone)} 자동 확정 예정
                      </span>
                    )}
                  </InfoRow>
                )}
                {/* 진행 중인 분쟁 — 구매자가 적은 사유 */}
                {order.status === 'DISPUTED' && order.disputeReason && (
                  <InfoRow label="분쟁 사유">
                    <span className="whitespace-pre-line">{order.disputeReason}</span>
                  </InfoRow>
                )}
                {/* 구매자가 분쟁 때 첨부한 증거 이미지 */}
                {order.disputeEvidenceCount > 0 && session && (
                  <InfoRow label="분쟁 증거">
                    <EvidenceGallery orderId={order.id} token={session.accessToken} />
                  </InfoRow>
                )}
                {/* 지난 분쟁 — 처리 결과와 사유 (진행 중이면 운영 액션에서 처리) */}
                {order.disputeResolution && (
                  <InfoRow label="분쟁">
                    <span className="font-semibold">
                      {DISPUTE_RESOLUTION_LABELS[order.disputeResolution]}
                    </span>
                    {order.disputeResolvedAt && (
                      <span className="ml-1.5 text-muted-foreground">
                        · {formatDateTime(order.disputeResolvedAt)}
                      </span>
                    )}
                    {order.disputeReason && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        접수 사유 — {order.disputeReason}
                      </p>
                    )}
                    {order.disputeResolutionNote && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        처리 사유 — {order.disputeResolutionNote}
                      </p>
                    )}
                  </InfoRow>
                )}
                {/* 그보다 앞선 분쟁 — 분쟁은 다시 열 수 있어 반복 신청인지 여기서 본다 */}
                {order.disputeHistory && order.disputeHistory.length > 0 && session && (
                  <InfoRow label={`이전 분쟁 ${order.disputeHistory.length}건`}>
                    <ul className="space-y-3">
                      {order.disputeHistory.map((d) => (
                        <li key={d.id} className="text-xs">
                          <div>
                            <span className="font-semibold text-foreground">
                              {d.resolution
                                ? DISPUTE_RESOLUTION_LABELS[d.resolution]
                                : '처리 기록 없음'}
                            </span>
                            <span className="ml-1.5 text-muted-foreground">
                              · 접수 {formatDateTime(d.disputedAt)}
                              {d.resolvedAt && ` · 처리 ${formatDateTime(d.resolvedAt)}`}
                            </span>
                          </div>
                          <p className="mt-1 whitespace-pre-line text-muted-foreground">
                            접수 사유 — {d.reason}
                          </p>
                          {d.resolutionNote && (
                            <p className="mt-0.5 text-muted-foreground">
                              처리 사유 — {d.resolutionNote}
                            </p>
                          )}
                          {d.evidenceCount > 0 && (
                            <EvidenceGallery
                              orderId={order.id}
                              token={session.accessToken}
                              recordId={d.id}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  </InfoRow>
                )}
                {/* 판매자 위반 신고 — 처리는 운영 액션에서, 여기서는 이력 */}
                {order.violationReports && order.violationReports.length > 0 && session && (
                  <InfoRow label={`판매자 위반 신고 ${order.violationReports.length}건`}>
                    <ul className="space-y-3">
                      {order.violationReports.map((r) => (
                        <li key={r.id} className="text-xs">
                          <div>
                            <span
                              className={`font-semibold ${r.resolution ? 'text-foreground' : 'text-rose-600'}`}
                            >
                              {r.resolution
                                ? VIOLATION_REPORT_RESOLUTION_LABELS[r.resolution]
                                : '처리 중'}
                            </span>
                            <span className="ml-1.5 text-muted-foreground">
                              · 접수 {formatDateTime(r.createdAt)}
                              {r.resolvedAt && ` · 처리 ${formatDateTime(r.resolvedAt)}`}
                            </span>
                          </div>
                          <p className="mt-1 whitespace-pre-line text-muted-foreground">
                            신고 사유 — {r.reason}
                          </p>
                          {r.resolutionNote && (
                            <p className="mt-0.5 text-muted-foreground">
                              처리 내용 — {r.resolutionNote}
                            </p>
                          )}
                          {r.evidenceCount > 0 && (
                            <EvidenceGallery
                              orderId={order.id}
                              token={session.accessToken}
                              reportId={r.id}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  </InfoRow>
                )}
              </Section>
            </>
          )}
          {tab === 'contract' && (
            <>
              {order.terms && <OrderContractTerms terms={order.terms} />}
              <OrderProposalHistory order={order} />
            </>
          )}
          {tab === 'license' &&
            (order.license || order.licenseDraft || order.status === OrderStatus.PAID ? (
              <LicenseTermsSection order={order} token={session.accessToken} onChanged={reload} />
            ) : (
              <p className="mt-8 text-sm text-muted-foreground">
                입금 확인 뒤 판매자가 발급 예정 내용을 올리면 여기에 보여요.
              </p>
            ))}
          {tab === 'actions' && (
            <OrderActionsPanel order={order} token={session.accessToken} onChanged={reload} />
          )}
          {tab === 'ledger' && (
            <>
              {ledger.length > 0 && (
                <Section title="원장" divided>
                  {ledger.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 py-3 text-sm">
                      <span className="w-36 shrink-0 text-xs tabular-nums text-muted-foreground">
                        {formatDateTime(e.createdAt)}
                      </span>
                      <span className="min-w-0 flex-1">{LEDGER_LABELS[e.type]}</span>
                      <span className="shrink-0 font-bold tabular-nums">
                        {e.amount.toLocaleString('ko-KR')}원
                      </span>
                    </div>
                  ))}
                </Section>
              )}
              {ledger.length === 0 && (
                <p className="mt-8 text-sm text-muted-foreground">원장 기록이 아직 없어요.</p>
              )}
            </>
          )}
          {tab === 'chat' && (
            <>
              <Section
                title="채팅"
                action={
                  room && (
                    <Link
                      to="/chats/$roomId"
                      params={{ roomId: room.id }}
                      className="text-xs font-semibold text-brand hover:underline"
                    >
                      채팅 관리에서 보기
                    </Link>
                  )
                }
              >
                {roomError && <ErrorBanner message={`채팅방을 불러오지 못했어요 — ${roomError}`} />}
                {room === undefined && !roomError && <LoadingRow />}
                {room && !room.viewerJoined && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/60 px-5 py-4 text-sm">
                      <span className="text-muted-foreground">
                        거래 채팅방이 열려 있어요. 어드민은 분쟁이 접수되면 자동으로 참여하고, 그
                        밖에는 참여하면 구매자·판매자와 직접 대화할 수 있어요. 대화 내역은 채팅
                        관리에서 볼 수 있어요.
                      </span>
                      <Button onClick={() => void handleRoomJoin()} disabled={roomJoining}>
                        {roomJoining ? '참여 중…' : '대화 참여'}
                      </Button>
                    </div>
                    {roomJoinError && <ErrorBanner message={roomJoinError} />}
                  </div>
                )}
                {room === null && (
                  <div className="rounded-lg bg-amber-50 px-5 py-4 text-sm text-amber-800">
                    아직 채팅방이 없어요 — 거래 채팅은 입금 확인(PAID) 후 열려요.
                    {order.status !== OrderStatus.DISPUTED && (
                      <div className="mt-1 text-xs text-amber-700">
                        어드민은 분쟁(DISPUTED) 상태로 전이된 주문에만 자동 참여해요.
                      </div>
                    )}
                  </div>
                )}
                {room?.viewerJoined && (
                  <AdminChatPanel
                    token={session.accessToken}
                    roomId={room.id}
                    senders={chatSendersOf(room.participants)}
                    onLeave={handleRoomLeave}
                  />
                )}
              </Section>
            </>
          )}
        </>
      )}
    </main>
  );
}
